import React from 'react'
import { Maybe } from '@zaptic-external/ts-monads'
import { v4 as uuidv4 } from 'uuid'
import isBoolean from 'lodash/isBoolean'
import type { ReportGuideDetails, ReportGuideId } from '../../../shared/domain/guides'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import { INITIAL_PROMPT_ANSWER, ZapBotModalPage, type PromptAnswer } from './ZapBot'
import type {
    AiChatResponseChunk,
    AiChatGuideSource,
    AiChatSource,
    AiChatUserContext,
    ChatId,
    ChatPromptId,
} from '../../../shared/domain/aiChats/types.no-deps'
import { route } from '../../../server/http/aiChats/addPrompt.no-deps'
import { ZapBotModalHeader } from './ZapBotModalHeader'
import { ZapBotModalBody } from './ZapBotModalBody'
import type { CommonDeps } from '../base/CommonDeps'
import type { AppFileGuideDetails, AppGuide, ParentInfo, WorkflowAppGuide } from '../guide/Model'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { type Nullable, assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { ModalBottomSheet } from '../ui/ModalBottomSheet'
import type { ApiClient } from '../../../app-core/lib/util/ApiClient'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import { ZapBotPromptInput } from './ZapBotPromptInput'
import { useCreditBalance } from '../creditBalance/useCreditBalance'
import { CreditBalanceSlug } from '../../../shared/domain/credits/types.no-deps'
import { last } from 'lodash'
import { ZapBotContextProvider } from './ZapBotContext'

interface ZapBotModalProps {
    allowImageZoom: boolean
    apiClient: ApiClient
    deps: CommonDeps
    featureFlags: Pick<CamelCaseFlags, 'aiZapBotFiles' | 'aiCredits'>
    fetchAppGuides: (guideSources: AiChatGuideSource[], withParentInfo: boolean) => Promise<AppGuide[]>
    fetchFileGuideDetails: (fileId: FileId) => Promise<AppFileGuideDetails>
    fetchReportGuideDetails: (reportGuideId: ReportGuideId) => Promise<ReportGuideDetails>
    fetchGuideParentInfo: (guideSource: AiChatGuideSource) => Promise<ParentInfo[]>
    fetchChatBotAnswer: (
        chatId: ChatId,
        input: string,
        context: Nullable<AiChatUserContext>
    ) => { responsePromise: Promise<Response>; controller: AbortController }
    fetchUserContext: () => AiChatUserContext
    onClose: () => void
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onClickSource?: (source: AiChatSource) => Promise<boolean>
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    open: boolean
    onReceiveSources?: (sources: AiChatSource[]) => Promise<void>
    renderCachedImage: RenderCachedImage
    renderWorkflowGuideExecution?: (guide: WorkflowAppGuide) => React.ReactElement<unknown>
    allowAnswerCopy?: boolean
    beta?: boolean
}

export function ZapBotModal(props: ZapBotModalProps) {
    const {
        allowImageZoom,
        apiClient,
        deps,
        featureFlags,
        fetchAppGuides,
        fetchFileGuideDetails,
        fetchReportGuideDetails,
        fetchGuideParentInfo,
        fetchChatBotAnswer,
        fetchUserContext,
        open,
        renderCachedImage,
        renderWorkflowGuideExecution,
        onClose,
        onClickGuideParentInfo,
        onClickSource,
        onOpenFile,
        onReceiveSources,
        allowAnswerCopy,
        beta,
    } = props

    const [maybeGuideToExecute, setMaybeGuideToExecute] = React.useState<Maybe<AiChatGuideSource>>(Maybe.nothing())
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const [answer, setAnswer] = React.useState<PromptAnswer>({ ...INITIAL_PROMPT_ANSWER })
    const promptRequestId = React.useRef(null)
    const promptRequestController = React.useRef(null)
    const [navHistory, setNavHistory] = React.useState<ZapBotModalPage[]>([])

    const currentPage = last(navHistory) || ZapBotModalPage.Main
    const isMainPage = currentPage === ZapBotModalPage.Main
    const showBackButton = !isMainPage
    const title = maybeGuideToExecute.map((source) => source.guide.name).withDefault(null)

    const [creditBalance, setCreditBalance] = useCreditBalance(
        deps,
        CreditBalanceSlug.AI,
        featureFlags.aiCredits && props.open
    )

    const getChatId = async () => {
        if (answer.chatId) {
            return answer.chatId
        }

        const response = await deps.httpClient.createAiChat()
        const { id: chatId } = response.chat

        setAnswer((state) => ({ ...state, chatId }))

        return chatId
    }

    const handleOnClickSource = async (source: AiChatSource) => {
        if (onClickSource && (await onClickSource(source))) {
            return // True prevents the default behavior
        }

        if (source.type === 'guide') {
            setMaybeGuideToExecute(Maybe.just(source))
            goToPage(ZapBotModalPage.GuideExecution)
        }
    }

    const handleOnBack = () => {
        setMaybeGuideToExecute(Maybe.nothing())
        setNavHistory((nav) => nav.slice(0, -1))
    }

    const handleOnClose = () => {
        setMaybeGuideToExecute(Maybe.nothing())
        setNavHistory([])
        onClose()
    }

    const chunkHandler = () => {
        const logger = deps.createLogger('Glbl_ZapBot.handleChunk')
        const textDecoder = new TextDecoder()

        let text = ''
        let sources: AiChatSource[] = []
        let promptId: ChatPromptId = null
        let predefinedAnswer: string = null

        return async (requestId: string, rawChunk: Uint8Array) => {
            if (!rawChunk.byteLength) {
                return
            }

            const parsedChunk: AiChatResponseChunk = JSON.parse(textDecoder.decode(rawChunk))

            switch (parsedChunk.type) {
                case 'llm-answer':
                    text += parsedChunk.data
                    break
                case 'sources':
                    sources = parsedChunk.data.map(coerceStreamedSource)

                    if (typeof onReceiveSources === 'function') {
                        await onReceiveSources(sources)
                    }

                    break
                case 'prompt-id':
                    promptId = parsedChunk.data
                    break
                case 'predefined-answer':
                    predefinedAnswer = parsedChunk.data
                    break
                case 'error':
                    throw Error('Received an error chunk in the stream')
                case 'debug-info':
                    logger.info('debug-info chunk data', parsedChunk.data)
                    break
                case 'credit-balance':
                    setCreditBalance(parsedChunk.data)
                    break
            }

            if (requestId === promptRequestId.current) {
                setAnswer((state) => ({
                    ...state,
                    promptId,
                    predefinedAnswer,
                    text,
                    sources: sources || [],
                    hasError: false,
                }))
            }
        }
    }

    const handleOnSubmitPrompt = async (prompt: string, files: FileId[]) => {
        if (!prompt.trim()) {
            return
        }

        const logger = deps.createLogger('Glbl_ZapBot.handleSend')

        setIsLoading(true)
        setAnswer((state) => ({
            ...state,
            text: '',
            predefinedAnswer: null,
            promptId: null,
            sources: [],
            hasError: false,
        }))

        promptRequestId.current = uuidv4()
        const chatId = await getChatId()

        // Prevents a race-condition in the rendering of responses from multiple questions sent by the user.
        const requestId = promptRequestId.current
        const userContext = {
            ...fetchUserContext(),
            ...(files.length > 0 && { chatFiles: files }),
        }

        try {
            const { responsePromise, controller } = fetchChatBotAnswer(chatId, prompt, userContext)
            promptRequestController.current = controller

            const response = await responsePromise

            if (!response.ok) {
                throw Error(
                    `Invalid ${route.method} ${route.pattern} response: ${response.status} - ${response.statusText}`
                )
            }

            const reader = response.body.getReader()
            const handleChunk = chunkHandler()
            let buffer: number[] = []

            await reader.read().then(async function parse({ done, value }): Promise<void> {
                if (done) {
                    setIsLoading(false)

                    return Promise.resolve()
                }

                if (requestId !== promptRequestId.current) {
                    controller.abort() // Aborts the stream if there was another call still going.

                    return Promise.resolve()
                }

                for (let i = 0; i < value.byteLength; i++) {
                    buffer.push(value[i])

                    const lastCharCodes = [
                        buffer[buffer.length - 3], // 125 - }
                        buffer[buffer.length - 2], // 10  - \n
                        buffer[buffer.length - 1], // 10  - \n
                    ]

                    if (lastCharCodes[0] === 125 && lastCharCodes[1] === 10 && lastCharCodes[2] === 10) {
                        await handleChunk(requestId, new Uint8Array(buffer))
                        buffer = []
                    }
                }

                return reader.read().then(parse)
            })
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error(`Error while generating response for question '${prompt}'. ${error.message}`)
                if (requestId === promptRequestId.current) {
                    setIsLoading(false)
                    setAnswer((state) => ({ ...state, hasError: true }))
                }
            }
        }
    }

    const handleOnSubmitFeedback = async (isGood: boolean, note: string) => {
        await deps.httpClient.updateAiPromptFeedback(answer.chatId, answer.promptId, {
            ...(isBoolean(isGood) && { isGood }),
            note,
        })
    }

    const handleOnAbort = () => {
        if (promptRequestController.current) {
            promptRequestController.current.abort()
        }
    }

    const goToPage = (page: ZapBotModalPage) => {
        if (page === ZapBotModalPage.Main) {
            // There is no back button in the main page, let's clear the nav history.
            return setNavHistory([])
        }

        if (currentPage !== page) {
            // Going to the same page the modal is at, shouldn't re-add to the history.
            setNavHistory((nav) => [...nav, page])
        }
    }

    return (
        <ZapBotContextProvider
            creditBalance={creditBalance}
            isLoading={isLoading}
            page={currentPage}
            goBack={handleOnBack}
            goToPage={goToPage}
            promptAnswer={answer}
            commonDeps={props.deps}
        >
            <ModalBottomSheet open={open} size="large" onClose={handleOnClose}>
                <div className="ZapBot_modal" data-testid="zapbot-modal" data-page={currentPage}>
                    <ZapBotModalHeader
                        onBack={handleOnBack}
                        onClose={handleOnClose}
                        featureFlags={featureFlags}
                        page={currentPage}
                        showBackButton={showBackButton}
                        title={title}
                        beta={beta}
                    />

                    <div hidden={!isMainPage}>
                        <ZapBotPromptInput
                            apiClient={apiClient}
                            deps={deps}
                            featureFlags={featureFlags}
                            fetchChatId={getChatId}
                            onSubmit={handleOnSubmitPrompt}
                            onAbort={handleOnAbort}
                        />
                    </div>

                    <div className="ZapBot_body">
                        <ZapBotModalBody
                            key={answer.promptId}
                            deps={deps}
                            allowImageZoom={allowImageZoom}
                            featureFlags={featureFlags}
                            fetchAppGuides={fetchAppGuides}
                            fetchFileGuideDetails={fetchFileGuideDetails}
                            fetchReportGuideDetails={fetchReportGuideDetails}
                            fetchGuideParentInfo={fetchGuideParentInfo}
                            maybeGuideToExecute={maybeGuideToExecute}
                            renderWorkflowGuideExecution={renderWorkflowGuideExecution}
                            onExecutionDone={handleOnBack}
                            onSourceClick={handleOnClickSource}
                            onClickGuideParentInfo={onClickGuideParentInfo}
                            onOpenFile={onOpenFile}
                            onOpenFileDone={handleOnBack}
                            onSubmitFeedback={handleOnSubmitFeedback}
                            renderCachedImage={renderCachedImage}
                            allowAnswerCopy={allowAnswerCopy}
                        />
                    </div>
                </div>
            </ModalBottomSheet>
        </ZapBotContextProvider>
    )
}

function coerceStreamedSource(source: AiChatSource) {
    // Dates inside streamed objects come through as strings, lets covert them to date objects
    switch (source.type) {
        case 'guide':
            return {
                ...source,
                guide: {
                    ...source.guide,
                    serverSyncTime: new Date(source.guide.serverSyncTime),
                },
            }
        case 'chatFile':
            return {
                ...source,
                file: {
                    ...source.file,
                    createdAt: new Date(source.file.createdAt),
                },
            }
        case 'report':
            return {
                ...source,
                report: {
                    ...source.report,
                    submittedDate: new Date(source.report.submittedDate),
                },
            }
        default:
            assumeExhaustion(source)
    }
}
